import type { IPublicClientApplication } from '@azure/msal-browser';
import type { QueryClient } from '@tanstack/react-query';
import { msalConfig } from '../config/authConfig';
import { LOGIN_HINT_KEY, SILENT_ATTEMPT_KEY } from './silentSignIn';

/**
 * Sign out, so a shared machine can be handed to the next person (Christian's signed-in walk, 2026-10-28; the shape is
 * ptc-omnichannel's account menu). FRONT-CHANNEL: `logoutRedirect` ends the Microsoft session itself, not just this
 * app's cache, because a cache-only sign-out is undone by the next tab's silent sign-in (`silentSignIn.ts`).
 *
 * Before leaving, it forgets this browser's login hint (it names the previous person), marks this tab's silent attempt
 * as made (the page the logout returns to shows the button rather than trying again), and drops every cached query, so
 * nothing of one person's clients is on screen for the next. What it keeps: the person's remembered client and
 * language live on the SERVER, per person, so they return when that person signs in again, and never for anyone else.
 */
export async function signOut(instance: IPublicClientApplication, queries: QueryClient): Promise<void> {
  try {
    localStorage.removeItem(LOGIN_HINT_KEY);
  } catch {
    /* no storage: nothing was remembered */
  }
  try {
    sessionStorage.setItem(SILENT_ATTEMPT_KEY, 'signed-out');
  } catch {
    /* without it a silent attempt fails anyway once the Microsoft session has ended */
  }
  queries.clear();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  await instance.logoutRedirect({
    ...(account ? { account } : {}),
    postLogoutRedirectUri: msalConfig.auth.redirectUri,
  });
}
